import type { createBrowserRouter } from 'react-router';
import { UI_TEXT } from '@/shared/languages';
import { getTitleSlots, subscribeTitleSlots } from '@/shared/navigation';
import { collectRouteTitles } from '@/shared/routing';

type AppRouter = ReturnType<typeof createBrowserRouter>;

export interface DocumentTitleDeps {
  router: AppRouter;
}

/**
 * `document.title` 을 현재 화면에 맞춰 갱신한다 — `헤빛온 · <화면명>`.
 *
 * SPA 는 문서가 한 번만 로드되므로 `index.html` 의 `<title>` 에 머물면 **모든 화면이 "헤빛온"** 이다.
 * 그 대가가 셋이다: 브라우저 탭·히스토리·북마크가 서로 구분되지 않고, **GA4 의 "페이지 제목" 측정기준이
 * 전부 같은 값이 되어 무용지물**이 된다.
 *
 * 화면명 출처는 **라우트 handle 타이틀 SSOT** — 셸 헤더가 쓰는 그 값이다. 매치 체인을 위에서 아래로 모아
 * (`collectRouteTitles`) 뎁스를 그대로 드러낸다. 타이틀 handle 이 없는 화면(auth·error 등)은 서비스명만 쓴다.
 *
 * 동적 이름은 **덮어쓰지 않고 마지막 마디로 붙인다** (`헤빛온 · 설비정보 · 발전소명`) — 헤더는 현재 화면
 * 하나를 보여주면 되지만 문서 제목은 위치를 알려야 한다. 출처는 두 슬롯:
 * `useSetHeaderTitle`(헤더도 함께 바뀜 — 발전소 상세) / `useSetTitleDetail`(문서 제목 전용 — 대시보드·
 * 발전량·인버터·수익처럼 화면 이름은 그대로고 선택 발전소만 덧붙는 경우).
 * 단 오버라이드는 렌더 이후(effect)에 설정되므로 **GA page_view 에는 실리지 않는다**(그 시점엔 정적 라우트
 * 타이틀까지만 나간다). 발전소명 같은 값이 GA 에 안 들어가는 건 오히려 의도에 맞는다.
 *
 * ⚠️ **`initAnalytics` 보다 먼저 배선할 것.** 라우터 구독자는 등록 순서대로 호출되므로, 뒤에 두면 GA 가
 * **직전 화면의 제목**을 `dt` 로 실어 보낸다.
 */
export function initDocumentTitle({ router }: DocumentTitleDeps): void {
  // `useMatches()` 는 handle 을 match 최상위에 두지만 `router.state.matches` 는 `match.route.handle` 이다 —
  // 공용 규칙(`collectRouteTitles`)에 넘기려고 route 로 펴 준다.
  const apply = (matches: AppRouter['state']['matches']) => {
    const parts = collectRouteTitles(matches.map((match) => match.route));
    // 동적 이름(발전소명 등)은 **덮어쓰지 않고 뒤에 붙인다** — 헤더는 현재 화면 하나만 보여주면 되지만
    // 문서 제목은 위치를 알려야 하므로 `설비정보 · 발전소명` 이 맞다.
    const { override, detail } = getTitleSlots();
    for (const part of [override, detail]) {
      if (part && parts[parts.length - 1] !== part) parts.push(part);
    }
    document.title = [UI_TEXT.COMMON_APP_NAME, ...parts].join(' · ');
  };

  apply(router.state.matches);
  router.subscribe((state) => {
    apply(state.matches);
  });
  // 보조 슬롯은 라우트 이동 없이도 바뀐다(데이터 도착 후 발전소명 세팅, 발전소 셀렉터 변경) → 별도 구독.
  subscribeTitleSlots(() => apply(router.state.matches));
}
